<template>
    <template v-if="farmName && farmName.length > 0">
        <ToolTip
            v-if="tooltipOnOverflow"
            :content="displayText"
            :disabled="!labelOverflows"
            :hover="prefersHoverTooltip"
            :show="prefersHoverTooltip ? null : labelTooltipOpen"
            :interactive="!prefersHoverTooltip"
            className="product-farm-name-tooltip mw-100"
            placement="bottom"
        >
            <span
                ref="labelRef"
                class="product-farm-name-label"
                :class="italicClass"
                @touchstart.passive="onLabelTouchStart"
                @touchend="onLabelTouchEnd"
                @touchcancel="onLabelTouchEnd"
                @touchmove.passive="onLabelTouchMove"
                @contextmenu="onLabelContextMenu"
            >{{ farmName }}<template v-if="isListK2K"> -</template></span>
        </ToolTip>
        <ToolTip v-else-if="showTooltip" :content="displayText">
            <span :class="italicClass">{{ farmName }}<template v-if="isListK2K"> -</template></span>
        </ToolTip>
        <span v-else :class="italicClass">{{ farmName }}<template v-if="isListK2K"> -</template></span>
    </template>
</template>
<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import ToolTip from './ToolTip.vue';
import { useTooltipHoverMode } from '@/composables/deviceType';

const LONG_PRESS_MS = 450;

const { prefersHoverTooltip } = useTooltipHoverMode();

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
    location: {
        type: String,
        required: false,
        default: '',
    },
    showTooltip: {
        type: Boolean,
        required: false,
        default: false,
    },
    /** Show full grower name in a tooltip only when the label is truncated (ellipsis). */
    tooltipOnOverflow: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const labelRef = ref(null);
const labelOverflows = ref(false);
const labelTooltipOpen = ref(false);

let longPressTimer = null;

const farmName = computed(() => {
    let programCode = props.product.program_code || props.product.programtype || '';
    programCode = programCode.toLowerCase();
    if (programCode == 'mmt') {
        return props.product.origin_name || props.product.mmt_origin_name || '';
    }

    const location = (props.location && props.location.toLowerCase()) || '';
    if (location == 'card' && !props.product.farm_name) {
        return '-';
    }

    return props.product.farm_name;
});

const displayText = computed(() => {
    const suffix = isListK2K.value ? ' -' : '';
    return `${farmName.value || ''}${suffix}`;
});

const italicClass = computed(() => {
    const location = (props.location && props.location.toLowerCase()) || '';
    return location == 'list' ? 'fst-italic' : '';
});

const isListK2K = computed(() => {
    return props.location && props.location.toLowerCase() == 'list' && props.product.is_k2k == 1;
});

const closeLabelTooltip = () => {
    labelTooltipOpen.value = false;
};

const clearLongPress = () => {
    if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
    }
};

const onLabelTouchStart = () => {
    if (prefersHoverTooltip.value || !labelOverflows.value) return;
    clearLongPress();
    longPressTimer = setTimeout(() => {
        labelTooltipOpen.value = true;
    }, LONG_PRESS_MS);
};

const onLabelTouchEnd = () => {
    clearLongPress();
};

const onLabelTouchMove = () => {
    clearLongPress();
};

const onLabelContextMenu = (event) => {
    if (!prefersHoverTooltip.value && labelOverflows.value) {
        event.preventDefault();
    }
};

const onDocumentDismissLabelTooltip = (event) => {
    if (!labelTooltipOpen.value) return;
    const el = labelRef.value;
    if (el && !el.contains(event.target)) {
        closeLabelTooltip();
    }
};

const checkLabelOverflow = () => {
    if (!props.tooltipOnOverflow) {
        labelOverflows.value = false;
        return;
    }
    const el = labelRef.value;
    if (!el) {
        labelOverflows.value = false;
        return;
    }
    if (el.scrollWidth > el.clientWidth) {
        labelOverflows.value = true;
        return;
    }
    const range = document.createRange();
    range.selectNodeContents(el);
    const textWidth = range.getBoundingClientRect().width;
    const boxWidth = el.getBoundingClientRect().width;
    labelOverflows.value = textWidth > boxWidth + 0.5;
};

const scheduleLabelOverflowCheck = () => {
    nextTick(checkLabelOverflow);
};

watch(displayText, scheduleLabelOverflowCheck);

watch(prefersHoverTooltip, (useHover) => {
    if (useHover) {
        closeLabelTooltip();
    }
});

watch(labelTooltipOpen, (isOpen) => {
    if (isOpen) {
        document.addEventListener('touchstart', onDocumentDismissLabelTooltip, { passive: true, capture: true });
        document.addEventListener('mousedown', onDocumentDismissLabelTooltip, { capture: true });
    } else {
        document.removeEventListener('touchstart', onDocumentDismissLabelTooltip, { capture: true });
        document.removeEventListener('mousedown', onDocumentDismissLabelTooltip, { capture: true });
    }
});

onMounted(() => {
    scheduleLabelOverflowCheck();
});

onUnmounted(() => {
    clearLongPress();
    document.removeEventListener('touchstart', onDocumentDismissLabelTooltip, { capture: true });
    document.removeEventListener('mousedown', onDocumentDismissLabelTooltip, { capture: true });
});
</script>

<style scoped>
.product-farm-name-label {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
